import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format, parseISO, isFuture, isPast } from "date-fns";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCountryFlag } from "@/lib/countryFlags";

const Races = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? "";

  const { data: races, isLoading } = useQuery({
    queryKey: ["races-2026"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("races")
        .select("*")
        .order("round_number", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Fetch published matchup counts and resolved status per race
  const { data: matchupInfo } = useQuery({
    queryKey: ["matchup-info"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("matchups")
        .select("race_id, id, status")
        .in("status", ["published", "resolved"]);
      if (error) throw error;
      const counts: Record<string, number> = {};
      const resolved: Record<string, boolean> = {};
      data?.forEach((m) => {
        counts[m.race_id] = (counts[m.race_id] ?? 0) + 1;
        if (m.status === "resolved") resolved[m.race_id] = true;
      });
      return { counts, resolved };
    },
  });

  // Fetch qualifying sessions to determine lock status
  const { data: qualiSessions } = useQuery({
    queryKey: ["quali-sessions-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sessions")
        .select("race_id, date_start")
        .eq("session_type", "Qualifying");
      if (error) throw error;
      const map: Record<string, string> = {};
      data?.forEach((s) => {
        map[s.race_id] = s.date_start;
      });
      return map;
    },
  });

  // Fetch user's picks counts per race
  const { data: userPickCounts } = useQuery({
    queryKey: ["user-pick-counts", userId],
    queryFn: async () => {
      const { data: matchupPicks, error: e1 } = await supabase
        .from("picks")
        .select("race_id, id")
        .eq("user_id", userId);
      if (e1) throw e1;

      const { data: racePicks, error: e2 } = await supabase
        .from("race_picks")
        .select("race_id, pole_driver_number, predicted_pole_margin, sprint_p1_driver_number, sprint_p2_driver_number, sprint_p3_driver_number")
        .eq("user_id", userId);
      if (e2) throw e2;

      const counts: Record<string, number> = {};
      matchupPicks?.forEach((p) => {
        counts[p.race_id] = (counts[p.race_id] ?? 0) + 1;
      });
      racePicks?.forEach((rp) => {
        const c = counts[rp.race_id] ?? 0;
        let extra = 0;
        if (rp.pole_driver_number) extra++;
        if (rp.sprint_p1_driver_number) extra++;
        if (rp.sprint_p2_driver_number) extra++;
        if (rp.sprint_p3_driver_number) extra++;
        counts[rp.race_id] = c + extra;
      });
      return counts;
    },
    enabled: !!userId,
  });

  const now = new Date();
  const nextRaceIndex = races?.findIndex((r) => r.status !== "completed" && r.status !== "cancelled" && isFuture(parseISO(r.date_end))) ?? -1;

  const getTipsBadge = (race: NonNullable<typeof races>[number]) => {
    if (!userId) return null;
    const matchupCount = matchupInfo?.counts[race.id] ?? 0;
    if (matchupCount === 0) return null;

    // Check if resolved
    if (matchupInfo?.resolved[race.id]) {
      return (
        <Badge className="text-[10px] bg-green-600 hover:bg-green-600 text-white flex items-center gap-0.5">
          <CheckCircle2 className="h-3 w-3" />
          Results
        </Badge>
      );
    }

    // Check if locked
    const qualiStart = qualiSessions?.[race.id];
    if (qualiStart && isPast(parseISO(qualiStart))) {
      return (
        <Badge variant="secondary" className="text-[10px] opacity-70">
          Locked
        </Badge>
      );
    }

    const totalRequired = matchupCount + 1 + (race.is_sprint_weekend ? 3 : 0);
    const userCount = userPickCounts?.[race.id] ?? 0;
    if (userCount === 0) return null;

    if (userCount >= totalRequired) {
      return (
        <Badge className="text-[10px] bg-green-600 hover:bg-green-600 text-white">
          Complete
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-[10px] border-yellow-500/50 text-yellow-600 dark:text-yellow-400">
        {userCount}/{totalRequired}
      </Badge>
    );
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-10">
      <h1 className="mb-1 text-2xl font-bold md:text-3xl">2026 Race Calendar</h1>
      <p className="mb-6 text-muted-foreground">
        {races?.length ?? "—"} rounds this season
      </p>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}

        {races?.map((race, idx) => {
          const isNext = idx === nextRaceIndex;
          const isCancelled = race.status === "cancelled";
          const isInactive = race.status === "completed" || isCancelled || isPast(parseISO(race.date_end));
          const flag = getCountryFlag(race.country_name);

          return (
            <Card
              key={race.id}
              className={cn(
                "transition-colors cursor-pointer hover:border-primary/40",
                isNext && "border-primary/60 ring-1 ring-primary/30",
                isInactive && "opacity-50"
              )}
              onClick={() => navigate(`/races/${race.id}`)}
            >
              <CardContent className="flex items-start gap-4 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-sm font-bold text-foreground">
                  R{race.round_number}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="truncate font-semibold leading-tight">
                      {race.meeting_name}
                    </h3>
                    {isNext && (
                      <Badge className="shrink-0 bg-primary text-primary-foreground text-[10px]">
                        NEXT
                      </Badge>
                    )}
                    {isCancelled ? (
                      <Badge variant="secondary" className="text-[10px] opacity-70">
                        CANCELLED
                      </Badge>
                    ) : (
                      getTipsBadge(race)
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {flag} {race.country_name}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{format(parseISO(race.date_start), "d MMM yyyy")}</span>
                    {race.is_sprint_weekend && (
                      <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
                        SPRINT
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Races;
